import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

import type { MinResourceConfig } from '@/pages/create-instance/utils/extraStorage'
import { formatRamMB } from '@/utils/formatter'

type MinimumResourceBadgeProps = {
  resource: MinResourceConfig
}

export const MINIMUM_RESOURCE_TOOLTIP_PROPS = {
  placement: 'top' as const,
  mouseEnterDelay: 0,
  color: 'rgba(14,18,27,0.8)',
  classNames: {
    root: 'max-w-[180px]',
  },
  styles: {
    body: {
      borderRadius: 8,
      maxWidth: 180,
      padding: 8,
      textAlign: 'center' as const,
    },
  },
}

function joinRequirementLabels(labels: string[], locale: string) {
  if (labels.length <= 1) return labels[0] ?? ''

  if (locale.toLowerCase().startsWith('ja')) {
    return labels.join('、')
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

function useMinimumResourceRequirement(resource: MinResourceConfig) {
  const { t, i18n } = useTranslation()
  const labels = [
    resource.cpuCores > 0
      ? t(
          resource.cpuCores === 1
            ? 'create.minimumRequirementVCpu'
            : 'create.minimumRequirementVCpus',
          '{{count}} vCPUs',
          { count: resource.cpuCores },
        )
      : '',
    resource.ramMB > 0 ? `${formatRamMB(resource.ramMB)} RAM` : '',
    resource.diskGB > 0
      ? t('create.minimumRequirementStorage', '{{count}} GB storage', {
          count: resource.diskGB,
        })
      : '',
  ].filter(Boolean)

  const requirements = joinRequirementLabels(labels, i18n.language)

  return {
    count: labels.length,
    text:
      labels.length > 0
        ? t('create.minimumRequirementsTooltip', 'Requires minimum {{requirements}}.', {
            requirements,
          })
        : '',
  }
}

export function MinimumResourceRequirementText({ resource }: MinimumResourceBadgeProps) {
  const requirement = useMinimumResourceRequirement(resource)

  return requirement.text || null
}

export function MinimumResourceBadge({ resource }: MinimumResourceBadgeProps) {
  const { t } = useTranslation()
  const requirement = useMinimumResourceRequirement(resource)

  if (!requirement.text) return null

  const badge = (
    <span className="inline-flex h-4 shrink-0 items-center justify-center gap-0.5 rounded-[36px] bg-[rgba(14,18,27,0.32)] px-1.5 pb-px t-xxs text-white whitespace-nowrap">
      {t('create.minRequirementsBadge', '{{count}} Min. Reqs', { count: requirement.count })}
    </span>
  )

  return (
    <Tooltip
      title={<div className="t-xs text-center text-white">{requirement.text}</div>}
      {...MINIMUM_RESOURCE_TOOLTIP_PROPS}
    >
      {badge}
    </Tooltip>
  )
}
